import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/AppLayout"
import { Head } from "@inertiajs/react"
import { IconArrowRight } from "@tabler/icons-react"

const LEVELS = [
  {
    name: "Junior",
    level: 1,
    slug: "junior",
    color: "green",
    description:
      "Desarrollador sin experiencia previa con clientes USA. En constante aprendizaje, contribuye en tareas de complejidad baja o media.",
  },
  {
    name: "Advanced",
    level: 2,
    slug: "advanced",
    color: "blue",
    description:
      "Desarrollador que puede brindar valor y producir en las tareas m\u00e1s comunes de un equipo de desarrollo web.",
  },
  {
    name: "Semi Senior",
    level: 3,
    slug: "semi-senior",
    color: "yellow",
    description:
      "Desarrollador con buen nivel de autonom\u00eda, que puede desarrollar gran parte del trabajo sin mayor asistencia.",
  },
  {
    name: "Senior",
    level: 4,
    slug: "senior",
    color: "purple",
    description: "Pr\u00f3ximamente se publicar\u00e1 la descripci\u00f3n detallada de este nivel.",
  },
  {
    name: "Staff",
    level: 5,
    slug: "staff",
    color: "red",
    description: "Pr\u00f3ximamente se publicar\u00e1 la descripci\u00f3n detallada de este nivel.",
  },
  {
    name: "Principal",
    level: 6,
    slug: "principal",
    color: "indigo",
    description: "Pr\u00f3ximamente se publicar\u00e1 la descripci\u00f3n detallada de este nivel.",
  },
]

const COLOR_CLASSES: Record<string, { bg: string; text: string; hover: string }> = {
  green: {
    bg: "bg-green-100 dark:bg-green-900/40",
    text: "text-green-600 dark:text-green-400",
    hover: "hover:border-green-300 dark:hover:border-green-700",
  },
  blue: {
    bg: "bg-blue-100 dark:bg-blue-900/40",
    text: "text-blue-600 dark:text-blue-400",
    hover: "hover:border-blue-300 dark:hover:border-blue-700",
  },
  yellow: {
    bg: "bg-yellow-100 dark:bg-yellow-900/40",
    text: "text-yellow-600 dark:text-yellow-400",
    hover: "hover:border-yellow-300 dark:hover:border-yellow-700",
  },
  purple: {
    bg: "bg-purple-100 dark:bg-purple-900/40",
    text: "text-purple-600 dark:text-purple-400",
    hover: "hover:border-purple-300 dark:hover:border-purple-700",
  },
  red: {
    bg: "bg-red-100 dark:bg-red-900/40",
    text: "text-red-600 dark:text-red-400",
    hover: "hover:border-red-300 dark:hover:border-red-700",
  },
  indigo: {
    bg: "bg-indigo-100 dark:bg-indigo-900/40",
    text: "text-indigo-600 dark:text-indigo-400",
    hover: "hover:border-indigo-300 dark:hover:border-indigo-700",
  },
}

export default function CareerPathway() {
  return (
    <>
      <Head title="Career Pathway - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>Growth & Career</BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Career Pathway</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <h1 className="text-xl font-bold">Career Pathway</h1>

        {/* Intro */}
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="text-lg">Nuestro Career Pathway</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-muted-foreground space-y-3 text-sm leading-relaxed">
              <p>
                Mediante <span className="text-primary font-semibold">6 seniorities</span>, se representa la
                progresi&oacute;n de madurez profesional de nuestros desarrolladores.
              </p>
              <p>
                Es importante entender que las descripciones de las etapas de madurez representan un marco general y un
                ideal de las habilidades, experiencias y contribuciones asociadas con cada etapa.
              </p>
              <p>Somos conscientes de que cada persona es &uacute;nica y el camino de cada uno es distinto.</p>
              <p>
                Valoramos la diversidad de experiencias y perspectivas que aportan cada uno de nosotros. Por ende, estas
                descripciones no son r&iacute;gidas y se adaptan a la singularidad de cada miembro de la
                organizaci&oacute;n.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Seniority Levels Grid */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900">
                <IconArrowRight className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <CardTitle className="text-lg">Niveles de Seniority</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {LEVELS.map((level) => {
                const colors = COLOR_CLASSES[level.color]
                return (
                  <a
                    key={level.slug}
                    href={`/growth/career-pathway/${level.slug}`}
                    className={`group block rounded-lg border p-5 transition-all hover:shadow-md ${colors.hover}`}
                  >
                    <div className="mb-3 flex items-center">
                      <div className={`mr-3 flex h-10 w-10 items-center justify-center rounded-full ${colors.bg}`}>
                        <span className="text-lg">
                          {["🐣", "🐥", "🦆", "🦅", "🐦\u200d\u2b1b", "🦉"][level.level - 1]}
                        </span>
                      </div>
                      <div>
                        <h3
                          className={`text-base font-bold group-hover:${colors.text.split(" ")[0].replace("text-", "text-")}`}
                        >
                          {level.name}
                        </h3>
                        <span className="text-muted-foreground text-xs">Nivel {level.level}</span>
                      </div>
                    </div>
                    <p className="text-muted-foreground text-sm">{level.description}</p>
                    <div className={`mt-3 flex items-center text-sm font-medium ${colors.text}`}>
                      Ver detalle
                      <IconArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </a>
                )
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

CareerPathway.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
